import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  campaignKindLabel,
  getPublicBrandMarket,
  PublicBrandMarketItem,
  PublicCampaignKind,
} from '../../api/payment/brand-public-market.api';
import { parseServerDate } from '../../utils/server-date';
import { ratingBadge } from '../../utils/brand-rating';
import { routePaths } from '../../routes/routes-path';
import { fetchAllCategory } from '../../api/user-profile/user-profile.api';
import { ICategory } from '../../models';
import type { BrandContentFormat } from '../../api/payment/brand-deal.api';
import { ContentFormatFilter } from '../../components/brand-deal/content-format-badge';

/**
 * Sàn chiến dịch công khai: ai cũng xem được các chiến dịch doanh nghiệp đang tìm creator (khách chưa đăng nhập cũng xem).
 * Nhận việc nằm ở trang chi tiết (/campaigns/:id), chỗ đó mới kiểm đăng nhập và điều kiện creator.
 */

export const formatVnd = (value: number) =>
  `${Number(value || 0).toLocaleString('vi-VN')}đ`;

/** Ngày dd/MM/yyyy theo giờ máy người xem; chuỗi hỏng thì rỗng. */
export const formatDay = (value?: string | null) =>
  parseServerDate(value)?.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }) ?? '';

const KIND_FILTERS: Array<{ value: PublicCampaignKind | ''; label: string }> = [
  { value: '', label: 'Tất cả' },
  { value: 'Direct', label: campaignKindLabel('Direct') },
  { value: 'Creative', label: campaignKindLabel('Creative') },
  { value: 'Proposal', label: campaignKindLabel('Proposal') },
];

// Khai ngoài component cha (tránh remount cả lưới mỗi lần render).
const CampaignCard: React.FC<{
  item: PublicBrandMarketItem;
  onOpen: () => void;
}> = ({ item, onOpen }) => {
  const thumb = item.images[0]?.url || null;
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
      className="ui-card flex cursor-pointer flex-col gap-2 overflow-hidden p-3 transition hover:shadow-md"
    >
      {thumb && (
        <div className="-mx-3 -mt-3 mb-1 aspect-[16/9] overflow-hidden bg-[#EAEFFA] dark:bg-slate-700">
          <img src={thumb} alt="" className="size-full object-cover" />
        </div>
      )}
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="ui-heading truncate text-[14px] font-bold">
          {item.brandName}
        </span>
        <span className="ui-muted shrink-0 text-[12px]">
          {ratingBadge(
            item.businessRatingAverage,
            item.businessRatingCount ?? 0
          )}
        </span>
      </div>
      <span className="w-fit rounded-full bg-[#EAEFFA] px-2 py-0.5 text-[11px] font-semibold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
        {campaignKindLabel(item.kind)}
      </span>
      <div className="ui-text line-clamp-2 text-[15px] font-semibold">
        {item.title}
      </div>
      {item.summary && (
        <p className="ui-muted line-clamp-2 text-[13px]">{item.summary}</p>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px]">
        <span className="font-bold text-emerald-600 dark:text-emerald-400">
          Creator nhận {formatVnd(item.netVnd)}
        </span>
        <span className="ui-muted">
          Còn {item.slotsLeft}/{item.slotsTotal} suất
        </span>
      </div>
      <div className="ui-muted flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]">
        {item.applyDeadline && (
          <span>Hạn nhận: {formatDay(item.applyDeadline)}</span>
        )}
        {item.minFollowers > 0 && (
          <span>
            Từ {item.minFollowers.toLocaleString('vi-VN')} người theo dõi
          </span>
        )}
      </div>
    </div>
  );
};

const CampaignMarketPage: React.FC = () => {
  const navigate = useNavigate();
  const [kind, setKind] = useState<PublicCampaignKind | ''>('');
  const [sort, setSort] = useState<'newest' | 'price'>('newest');
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [format, setFormat] = useState<BrandContentFormat | ''>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PublicBrandMarketItem[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  // Đổi bộ lọc khi lượt trước chưa về thì bỏ kết quả cũ, tránh lưới hiện nhầm loại.
  const requestSeq = useRef(0);

  const load = useCallback(
    (nextPage: number) => {
      requestSeq.current += 1;
      const seq = requestSeq.current;
      const append = nextPage > 1;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(false);
      getPublicBrandMarket({
        type: kind || undefined,
        categoryId: categoryId || undefined,
        format: format || undefined,
        sort,
        page: nextPage,
      })
        .then((res) => {
          if (seq !== requestSeq.current) return;
          setItems((prev) => (append ? [...prev, ...res.items] : res.items));
          setHasNextPage(res.hasNextPage);
          setPage(nextPage);
        })
        .catch(() => {
          if (seq === requestSeq.current) setError(true);
        })
        .finally(() => {
          if (seq !== requestSeq.current) return;
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [kind, categoryId, format, sort]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  // Danh sách chủ đề không tải được thì ẩn ô lọc chủ đề, các bộ lọc khác vẫn dùng bình thường.
  useEffect(() => {
    fetchAllCategory()
      .then((list) => setCategories(Array.isArray(list) ? list : []))
      .catch(() => undefined);
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-4">
      <div>
        <h1 className="ui-heading text-[22px] font-bold">Sàn chiến dịch</h1>
        <p className="ui-muted mt-1 text-[14px]">
          Chiến dịch doanh nghiệp đang tìm creator. Ai cũng xem được, nhận việc
          cần tài khoản creator.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {KIND_FILTERS.map((f) => {
          const active = kind === f.value;
          return (
            <button
              key={f.label}
              type="button"
              aria-pressed={active}
              onClick={() => setKind(f.value)}
              className={`rounded-full border px-3 py-1.5 text-[13px] font-semibold ${
                active
                  ? 'border-[#0057FF] bg-[#0057FF] text-white'
                  : 'ui-divider ui-text'
              }`}
            >
              {f.label}
            </button>
          );
        })}
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {categories.length > 0 && (
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              aria-label="Lọc theo chủ đề"
              className="rounded-md border ui-divider bg-transparent px-2 py-1.5 text-[13px] outline-none ui-text"
            >
              <option value="">Mọi chủ đề</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <ContentFormatFilter
            value={format}
            onChange={(v) => setFormat(v as BrandContentFormat | '')}
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as 'newest' | 'price')}
            aria-label="Sắp xếp"
            className="rounded-md border ui-divider bg-transparent px-2 py-1.5 text-[13px] outline-none ui-text"
          >
            <option value="newest">Mới nhất</option>
            <option value="price">Tiền cao nhất</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="animate-spin ui-muted" />
        </div>
      ) : error && items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 p-6 text-[14px]">
          <p className="ui-muted">Không tải được danh sách chiến dịch.</p>
          <button
            type="button"
            onClick={() => load(1)}
            className="rounded-md border ui-divider px-3 py-1 ui-text"
          >
            Thử lại
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="ui-muted p-6 text-center text-[14px]">
          {kind || categoryId || format
            ? 'Không có chiến dịch nào khớp bộ lọc.'
            : 'Hiện chưa có chiến dịch nào đang mở.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <CampaignCard
              key={item.campaignId}
              item={item}
              onOpen={() =>
                navigate(`${routePaths.campaigns}/${item.campaignId}`)
              }
            />
          ))}
        </div>
      )}

      {!loading && hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            disabled={loadingMore}
            onClick={() => load(page + 1)}
            className="rounded-md border ui-divider px-4 py-1.5 text-[14px] ui-text disabled:opacity-60"
          >
            {loadingMore ? 'Đang tải…' : 'Xem thêm'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CampaignMarketPage;
